<template>
	<div>
		<UProgress
			v-if="loading"
			color="secondary"
			class="max-w-sm w-full mx-auto"
		/>
		<div v-else>
			<div
				v-if="loggedIn"
				class="flex pt-14"
			>
				<UButton
					:to="`${accountLinkPage || accountUrl}`"
					class="px-6 py-4 m-auto"
					size="lg"
					color="primary"
				>
					{{ accountLinkText }}
				</UButton>
			</div>
			<div v-else>
				<UForm
					:schema="schema"
					:state="state"
					class="flex flex-col w-full space-y-4 items-center"
					@submit="onSubmit"
				>
					<div class="flex flex-col sm:flex-row space-y-8 sm:space-x-4 justify-between w-full">
						<UFormField
							:label="$t('login.username')"
							name="email"
							class="w-full"
						>
							<UInput
								v-model="state.email"
								class="w-full"
							/>
						</UFormField>

						<UFormField
							:label="$t('login.password')"
							name="password"
							class="w-full"
						>
							<UInput
								v-model="state.password"
								:type="showPassword ? 'text' : 'password'"
								:ui="{ trailing: 'pe-1' }"
								class="w-full"
							>
								<template #trailing>
									<UButton
										color="neutral"
										variant="link"
										size="sm"
										:icon="showPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'"
										:aria-label="showPassword ? 'Hide password' : 'ShowPassword password'"
										:aria-pressed="showPassword"
										aria-controls="password"
										@click="showPassword = !showPassword"
									/>
								</template>
							</UInput>
						</UFormField>
					</div>
					<UButton
						:label="submitButtonText"
						loading-auto
						type="submit"
						size="xl"
						color="primary"
						class="w-full max-w-sm flex justify-center uppercase mt-8 sm:mt-0"
					/>
				</UForm>
				<div class="w-full text-center mt-6">
					<UButton
						:to="localePath('/forgot-password')"
						class="link"
						color="primary"
						variant="ghost"
					>
						{{ $t('login.forgot') }}
					</UButton>
				</div>
			</div>
		</div>
		<ModalTwoFactorAuthentication
			v-model="modalOpen"
			@confirm="handle2FAVerification"
		/>
	</div>
</template>

<script setup lang="ts">
import { useAuth } from '@/composables/useAuth'
import { useLogin } from '@/composables/useLogin'
import { useZod } from '@/composables/useZod'
import type { FormSubmitEvent } from '@nuxt/ui'
import { useI18n } from 'vue-i18n'
import * as z from 'zod'

const localePath = useLocalePath()

const {
	zEmail, zPassword
} = useZod()

const modalOpen = ref(false)
const { login } = useLogin(modalOpen)
const {
	loggedIn, accountUrl, loading
} = useAuth()
const { t } = useI18n({
	useScope: 'global'
})

const props = defineProps({
	customSubmitText: {
		type: String,
		default: ''
	},
	customAccountLinkText: {
		type: String,
		default: ''
	},
	accountLinkPage: {
		type: String,
		default: ''
	}
})

const {
	customSubmitText, customAccountLinkText, accountLinkPage
} = toRefs(props)

const schema = z.object({
	email: zEmail,
	password: zPassword
})

type Schema = z.output<typeof schema>

const state = reactive<Partial<Schema>>({
	email: '',
	password: ''
})

const showPassword = ref(false)

const submitButtonText = computed(() => {
	return customSubmitText.value || t('login.button-text')
})

const accountLinkText = computed(() => {
	return customAccountLinkText.value || t('login.view-account')
})

const onSubmit = async (event: FormSubmitEvent<Schema>) => {
	await login(event.data.email, event.data.password)
}
const handle2FAVerification = async (otp: number) => {
	await login(state.email as string, state.password as string, otp)
	modalOpen.value = false
}
</script>
